import React from 'react';
import { Link } from 'react-router-dom';
import HeroSlider from '../components/HeroSlider';
import ProductCard from '../components/ProductCard';
import { products, services, stats, blogPosts } from '../data/mockData';
import { Shield, Wrench, Headphones, Star, CheckCircle, ArrowRight, Award, Factory, Clock } from 'lucide-react';

const serviceIcons = {
  headset: Headphones,
  wrench: Wrench,
  shield: Shield,
};

const HomePage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Slider */}
      <HeroSlider />

      {/* Stats Bar */}
      <div className="bg-[#1a1a1a] py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl font-black text-orange-500 mb-1">{stat.value}</div>
                <div className="text-gray-400 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Services Badges */}
      <div className="bg-gray-50 py-10 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {services.map((service) => {
              const Icon = serviceIcons[service.icon] || Shield;
              return (
                <Link
                  to={service.link}
                  key={service.id}
                  className="flex items-center gap-4 bg-white rounded-xl p-5 shadow-sm border border-gray-100 hover:border-orange-200 hover:shadow-md transition-all duration-200 group"
                >
                  <div className="w-16 h-16 flex-shrink-0">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="hidden w-16 h-16 bg-orange-100 rounded-full items-center justify-center">
                      <Icon size={28} className="text-orange-500" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 group-hover:text-orange-600 transition-colors">{service.title}</h3>
                    <p className="text-gray-500 text-sm mt-0.5">{service.description}</p>
                  </div>
                  <ArrowRight size={16} className="ml-auto text-gray-300 group-hover:text-orange-500 group-hover:translate-x-1 transition-all duration-200" />
                </Link>
              );
            })}
          </div>
        </div>
      </div>

      {/* Products Section */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block bg-orange-100 text-orange-600 text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded mb-3">
              Notre gamme
            </span>
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-3">
              Nos Stores & Pergolas
            </h2>
            <p className="text-gray-500 max-w-xl mx-auto">
              Découvrez notre gamme complète de stores et pergolas de qualité professionnelle, fabriqués sur mesure dans notre usine française.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {products.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>

          <div className="text-center mt-10">
            <Link
              to="/nos-stores"
              className="inline-flex items-center gap-2 border-2 border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white font-bold px-8 py-3.5 rounded-lg transition-all duration-200"
            >
              Voir tous nos produits
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* About / Quality Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Image */}
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="https://stores-shop.com/wp-content/uploads/2020/01/store-banne-model-c2-scaled.jpg"
                  alt="Store banne fabrication"
                  className="w-full h-80 object-cover"
                  onError={(e) => { e.target.src = 'https://images.pexels.com/photos/14212807/pexels-photo-14212807.jpeg?w=800'; }}
                />
              </div>
              {/* Floating badge */}
              <div className="absolute -bottom-5 -right-5 bg-orange-500 text-white rounded-xl p-4 shadow-xl">
                <div className="text-3xl font-black">12</div>
                <div className="text-xs font-semibold uppercase tracking-wide">Ans de</div>
                <div className="text-xs font-semibold uppercase tracking-wide">Garantie</div>
              </div>
            </div>

            {/* Right: Content */}
            <div>
              <span className="inline-block bg-orange-100 text-orange-600 text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded mb-4">
                Notre engagement qualité
              </span>
              <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-5">
                Un parti pris de <span className="text-orange-500">qualité</span>
              </h2>
              <p className="text-gray-600 mb-4 leading-relaxed">
                Nous fabriquons des stores en utilisant de l'aluminium « Première Fusion », des toiles
                100% acrylique, une visserie en Inox. En matière d'électronique, nous privilégions
                fiabilité et réparabilité en sélectionnant les produits de la maison <strong>SOMFY™</strong>.
              </p>
              <p className="text-gray-600 mb-6 leading-relaxed">
                <strong>Stores & Pergolas™</strong> est spécialiste et fabricant français de stores et de
                toiles sur-mesure <strong>depuis 1986</strong>. C'est dans le sud de la France, entre
                Aix en Provence et Marseille, que sont fabriqués nos stores bannes extérieurs de haute
                qualité à prix d'usine.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                {[
                  { icon: Award, text: 'Aluminium Première Fusion' },
                  { icon: CheckCircle, text: 'Toile 100% acrylique' },
                  { icon: Shield, text: 'Garantie 12 ans' },
                  { icon: Factory, text: 'Usine 2000m² en France' },
                  { icon: Star, text: 'Motorisation Somfy®' },
                  { icon: Clock, text: 'Depuis 1986' },
                ].map(({ icon: Icon, text }, i) => (
                  <div key={i} className="flex items-center gap-2.5 text-gray-700">
                    <div className="w-7 h-7 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                      <Icon size={14} className="text-orange-500" />
                    </div>
                    <span className="text-sm font-medium">{text}</span>
                  </div>
                ))}
              </div>

              <Link
                to="/qui-sommes-nous"
                className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-6 py-3 rounded-lg transition-all duration-200 shadow-md hover:shadow-orange-200"
              >
                En savoir plus sur nous
                <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Partners */}
      <section className="py-10 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4">
          <p className="text-center text-xs uppercase tracking-widest text-gray-400 mb-6">Nos partenaires de confiance</p>
          <div className="flex items-center justify-center flex-wrap gap-12">
            {['SOMFY®', 'LLAZA®', 'DICKSON®'].map((brand) => (
              <div key={brand} className="text-2xl font-black text-gray-300 hover:text-gray-500 transition-colors cursor-pointer tracking-widest">
                {brand}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-end justify-between mb-10">
            <div>
              <span className="inline-block bg-orange-100 text-orange-600 text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded mb-3">
                Nos conseils
              </span>
              <h2 className="text-3xl font-black text-gray-900">Actualités & Blog</h2>
            </div>
            <Link
              to="/blog"
              className="hidden md:flex items-center gap-1.5 text-orange-500 font-semibold hover:gap-3 transition-all text-sm"
            >
              Voir tous les articles <ArrowRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {blogPosts.map((post) => (
              <article key={post.id} className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-200 group border border-gray-100">
                <div className="overflow-hidden h-48">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => { e.target.src = 'https://images.pexels.com/photos/14212807/pexels-photo-14212807.jpeg?w=600'; }}
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xs bg-orange-100 text-orange-600 font-semibold px-2 py-0.5 rounded">{post.category}</span>
                    <span className="text-xs text-gray-400">{post.date}</span>
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2 group-hover:text-orange-600 transition-colors">{post.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed mb-4">{post.excerpt}</p>
                  <Link
                    to="/blog"
                    className="text-sm text-orange-500 font-semibold flex items-center gap-1.5 hover:gap-3 transition-all"
                  >
                    Lire l'article <ArrowRight size={13} />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#1a1a1a]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-black text-white mb-4">
            Prêt à équiper votre terrasse ?
          </h2>
          <p className="text-gray-400 text-lg mb-8">
            Obtenez un devis gratuit et personnalisé pour votre projet de store ou pergola.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-8 py-4 rounded-lg transition-all duration-200 shadow-lg hover:shadow-orange-500/30 text-lg"
            >
              Demander un devis gratuit
              <ArrowRight size={18} />
            </Link>
            <Link
              to="/nos-stores"
              className="inline-flex items-center gap-2 border-2 border-white/30 hover:border-white text-white font-semibold px-8 py-4 rounded-lg transition-all duration-200 text-lg"
            >
              Voir nos produits
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
